import {
  addCalendarDaysToDateStr,
  dateStrInAppTz,
  dayBoundsInAppTz,
  formatCalendarDateShortTr,
  noonInAppTzDate,
} from "./appTime";

export const PERIOD_MODES = ["day", "week", "month", "custom"];

const MONTHS_TR = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

/** Monday = 0 … Sunday = 6 (Europe/Istanbul calendar day). */
export function dayOfWeekMondayFirst(dateStr) {
  const jsDay = noonInAppTzDate(dateStr).getUTCDay();
  return (jsDay + 6) % 7;
}

export function todayPeriodStr() {
  return dateStrInAppTz(new Date());
}

export function weekStartMonday(dateStr) {
  const offset = dayOfWeekMondayFirst(dateStr);
  return addCalendarDaysToDateStr(dateStr, -offset);
}

export function weekEndSunday(dateStr) {
  return addCalendarDaysToDateStr(weekStartMonday(dateStr), 6);
}

export function monthStart(dateStr) {
  return `${dateStr.slice(0, 7)}-01`;
}

export function monthEnd(dateStr) {
  const [y, m] = dateStr.split("-").map(Number);
  const next =
    m === 12
      ? `${y + 1}-01-01`
      : `${y}-${String(m + 1).padStart(2, "0")}-01`;
  return addCalendarDaysToDateStr(next, -1);
}

export function resolvePeriodRange(mode, anchorDate, customRange) {
  if (mode === "custom" && customRange?.startDate && customRange?.endDate) {
    const a = customRange.startDate;
    const b = customRange.endDate;
    return a <= b ? { startDate: a, endDate: b } : { startDate: b, endDate: a };
  }
  const anchor = anchorDate || todayPeriodStr();
  if (mode === "week") {
    return {
      startDate: weekStartMonday(anchor),
      endDate: weekEndSunday(anchor),
    };
  }
  if (mode === "month") {
    return {
      startDate: monthStart(anchor),
      endDate: monthEnd(anchor),
    };
  }
  return { startDate: anchor, endDate: anchor };
}

export function shiftPeriodAnchor(mode, anchorDate, direction) {
  if (mode === "custom") return anchorDate;
  const delta = direction > 0 ? 1 : -1;
  if (mode === "day") {
    return addCalendarDaysToDateStr(anchorDate, delta);
  }
  if (mode === "week") {
    return addCalendarDaysToDateStr(anchorDate, delta * 7);
  }
  if (mode === "month") {
    const [y, m, d] = anchorDate.split("-").map(Number);
    let nm = m + delta;
    let ny = y;
    while (nm < 1) {
      nm += 12;
      ny -= 1;
    }
    while (nm > 12) {
      nm -= 12;
      ny += 1;
    }
    const anchor = `${ny}-${String(nm).padStart(2, "0")}-01`;
    const lastDay = Number(monthEnd(anchor).split("-")[2]);
    const nd = Math.min(d, lastDay);
    return `${ny}-${String(nm).padStart(2, "0")}-${String(nd).padStart(2, "0")}`;
  }
  return anchorDate;
}

export function isCurrentPreset(mode, startDate, endDate) {
  if (mode === "custom") return false;
  const current = resolvePeriodRange(mode, todayPeriodStr());
  return startDate === current.startDate && endDate === current.endDate;
}

export function periodNavLabel(mode, anchorDate, startDate, endDate) {
  if (mode === "custom") return periodRangeSummary(startDate, endDate);
  if (isCurrentPreset(mode, startDate, endDate)) {
    if (mode === "day") return "Bugün";
    if (mode === "week") return "Bu hafta";
    if (mode === "month") return "Bu ay";
  }
  if (mode === "day") return formatCalendarDateShortTr(anchorDate);
  if (mode === "week") {
    if (startDate.slice(0, 7) === endDate.slice(0, 7)) {
      const startDay = Number(startDate.split("-")[2]);
      const endDay = Number(endDate.split("-")[2]);
      const monthIdx = Number(startDate.split("-")[1]) - 1;
      return `${startDay}–${endDay} ${MONTHS_TR[monthIdx] ?? ""}`.trim();
    }
    return `${formatCalendarDateShortTr(startDate)} – ${formatCalendarDateShortTr(endDate)}`;
  }
  if (mode === "month") {
    const [y, m] = anchorDate.split("-");
    return `${MONTHS_TR[Number(m) - 1] ?? "—"} ${y}`;
  }
  return periodRangeSummary(startDate, endDate);
}

export function periodRangeSummary(startDate, endDate) {
  if (!startDate || !endDate) return "—";
  if (startDate === endDate) return formatCalendarDateShortTr(startDate);
  return `${formatCalendarDateShortTr(startDate)} – ${formatCalendarDateShortTr(endDate)}`;
}

export function canNavigatePeriodForward(
  mode,
  anchorDate,
  maxDate = todayPeriodStr(),
  customRange,
) {
  if (mode === "custom") return false;
  const { endDate } = resolvePeriodRange(mode, anchorDate, customRange);
  return endDate < maxDate;
}

export function toPeriodISOParams(startDate, endDate) {
  return {
    startDate: dayBoundsInAppTz(startDate).start.toISOString(),
    endDate: dayBoundsInAppTz(endDate).end.toISOString(),
  };
}

export function createPeriodState(
  mode = "day",
  anchorDate = todayPeriodStr(),
  customRange,
) {
  const { startDate, endDate } = resolvePeriodRange(
    mode,
    anchorDate,
    customRange,
  );
  return {
    mode,
    anchorDate: mode === "custom" ? startDate : anchorDate || todayPeriodStr(),
    startDate,
    endDate,
  };
}

export function isDefaultActivitiesPeriod({ mode, anchorDate }) {
  return mode === "day" && anchorDate === todayPeriodStr();
}
